<!-- Комбинированное поле названия строки ремонта с поиском по каталогу. -->
<template>
  <div ref="root" class="repair-item-name-combobox">
    <base-input
      ref="input"
      :id="id"
      :model-value="modelValue"
      :placeholder="placeholder"
      :invalid="invalid"
      :clearable="true"
      autocomplete="off"
      @blur="handleBlur"
      @focus="openSuggestions"
      @clear="handleClear"
      @keydown="handleKeydown"
      @update:model-value="handleInput"
    />

    <Teleport to="body">
      <!-- Dropdown живет в body, чтобы не обрезаться overflow-ом формы/модалки. -->
      <div
        v-if="isOpen"
        ref="dropdown"
        class="repair-item-name-combobox__dropdown"
        :style="dropdownStyles"
        @mousedown.prevent
      >
        <div
          v-if="isLoading"
          class="repair-item-name-combobox__empty"
        >
          Загрузка...
        </div>

        <template v-else>
          <button
            v-for="(catalogItem, optionIndex) in catalogItems"
            :key="catalogItem.id"
            type="button"
            class="repair-item-name-combobox__option"
            :class="{
              'repair-item-name-combobox__option--highlighted':
                optionIndex === highlightedIndex,
            }"
            @mouseenter="highlightedIndex = optionIndex"
            @mousedown.prevent="selectCatalogItem(catalogItem)"
          >
            <span class="repair-item-name-combobox__option-main">
              <strong>{{ catalogItem.name }}</strong>
              <span>{{ itemTypeLabel(catalogItem.base_type) }}</span>
            </span>
            <span class="repair-item-name-combobox__option-meta">
              <span v-if="catalogItem.number">
                {{ catalogItem.number }}
              </span>
              <span v-if="catalogItem.unit_price">
                {{ formatPrice(catalogItem.unit_price) }}
              </span>
              <span v-if="catalogItem.unit">{{ catalogItem.unit }}</span>
              <span>{{ vatLabel(catalogItem) }}</span>
            </span>
          </button>

          <div
            v-if="catalogItems.length === 0"
            class="repair-item-name-combobox__empty"
          >
            Ничего не найдено. Название сохранится как ручной ввод.
          </div>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { useCatalogItemStore } from "@/stores/catalogItemStore";

export default {
  name: "FleetRepairItemNameCombobox",

  props: {
    id: {
      type: String,
      default: null,
    },
    modelValue: {
      type: [String, Number],
      default: "",
    },
    itemType: {
      type: String,
      default: null,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    itemTypeOptions: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: "Что сделали или купили",
    },
  },

  emits: ["lookup-change", "select", "update:modelValue"],

  data() {
    return {
      isOpen: false,
      highlightedIndex: -1,
      dropdownStyles: {},
      catalogItems: [],
      isLoading: false,
      searchTimeout: null,
    };
  },

  computed: {
    catalogItemStore() {
      return useCatalogItemStore();
    },

    searchText() {
      return String(this.modelValue || "").trim();
    },

    highlightedCatalogItem() {
      return this.catalogItems[this.highlightedIndex] || null;
    },
  },

  watch: {
    itemType() {
      this.scheduleSearch();
    },
  },

  mounted() {
    // Dropdown вынесен в body и позиционируется вручную, поэтому при
    // resize/scroll нужно пересчитывать координаты относительно input.
    window.addEventListener("resize", this.updateDropdownPosition);
    window.addEventListener("scroll", this.updateDropdownPosition, true);
    document.addEventListener("mousedown", this.handleDocumentMouseDown);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.updateDropdownPosition);
    window.removeEventListener("scroll", this.updateDropdownPosition, true);
    document.removeEventListener(
      "mousedown",
      this.handleDocumentMouseDown
    );
    window.clearTimeout(this.searchTimeout);
  },

  methods: {
    handleInput(value) {
      this.$emit("update:modelValue", value);
      this.emitLookupChange({
        hasExactMatch: false,
        isLoading: true,
        searchText: String(value || "").trim(),
      });
      this.openSuggestions();
      this.scheduleSearch();
    },

    handleClear() {
      this.$emit("update:modelValue", "");
      this.$emit("select", null);
      this.emitLookupChange({
        hasExactMatch: false,
        isLoading: false,
        searchText: "",
      });
      this.catalogItems = [];
      this.closeSuggestions();
    },

    handleBlur() {
      window.setTimeout(this.closeSuggestions, 120);
    },

    openSuggestions() {
      this.isOpen = true;
      this.$nextTick(this.updateDropdownPosition);
      this.scheduleSearch();
    },

    closeSuggestions() {
      this.isOpen = false;
      this.highlightedIndex = -1;
    },

    scheduleSearch() {
      window.clearTimeout(this.searchTimeout);
      this.searchTimeout = window.setTimeout(this.fetchCatalogItems, 220);
    },

    async fetchCatalogItems() {
      // Подсказки для строки ремонта ищем только среди активных ремонтных
      // позиций каталога.
      const params = {
        "q[usage_scope]": "repair",
        "q[archived]": "active",
      };

      // Текст и тип строки ремонта сужают список вариантов в dropdown.
      if (this.searchText) params["q[search]"] = this.searchText;
      const baseType = this.catalogBaseTypeForRepairItemType(
        this.itemType
      );
      if (baseType) {
        params["q[base_type]"] = baseType;
      }

      this.isLoading = true;
      try {
        // Новая выдача не должна сохранять подсвеченный индекс от
        // предыдущего списка.
        this.catalogItems =
          await this.catalogItemStore.fetchCatalogItemSelectItems(params);
        this.highlightedIndex = -1;
        this.isLoading = false;
        this.emitLookupChange();
        this.$nextTick(this.updateDropdownPosition);
      } finally {
        this.isLoading = false;
      }
    },

    selectHighlighted() {
      if (!this.highlightedCatalogItem) return;

      this.selectCatalogItem(this.highlightedCatalogItem);
    },

    handleKeydown(event) {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.highlightNext();
          break;

        case "ArrowUp":
          event.preventDefault();
          this.highlightPrevious();
          break;

        case "Enter":
          if (!this.isOpen) return;

          event.preventDefault();
          event.stopPropagation();
          if (!this.highlightedCatalogItem) {
            this.closeSuggestions();
            return;
          }

          this.selectHighlighted();
          break;

        case "Escape":
          if (!this.isOpen) return;

          event.preventDefault();
          event.stopPropagation();
          this.closeSuggestions();
          break;
      }
    },

    selectCatalogItem(catalogItem) {
      this.$emit("update:modelValue", catalogItem.name);
      this.emitLookupChange({
        hasExactMatch: true,
        isLoading: false,
        searchText: catalogItem.name,
      });
      this.$emit("select", this.repairItemSnapshot(catalogItem));
      this.closeSuggestions();
    },

    emitLookupChange(overrides = {}) {
      // Родитель по этому состоянию показывает: найдено в каталоге,
      // будет создано в каталоге или останется ручным вводом.
      this.$emit("lookup-change", {
        hasExactMatch: this.hasExactCatalogItemMatch(),
        isLoading: this.isLoading,
        searchText: this.searchText,
        ...overrides,
      });
    },

    hasExactCatalogItemMatch() {
      const searchText = this.normalizedText(this.searchText);
      if (!searchText) return false;

      return this.catalogItems.some(
        (catalogItem) =>
          this.normalizedText(catalogItem.name) === searchText
      );
    },

    repairItemSnapshot(catalogItem) {
      // Строка ремонта хранит snapshot каталожной позиции, чтобы история
      // ремонта не менялась при последующем редактировании каталога.
      return {
        catalog_item_id: catalogItem.id,
        item_type: this.repairItemTypeForCatalogBaseType(
          catalogItem.base_type
        ),
        name: catalogItem.name,
        unit: catalogItem.unit || "",
        unit_price: catalogItem.unit_price ?? "",
        vat_kind: catalogItem.vat_kind || "no_vat",
        vat_rate: catalogItem.vat_rate ?? "",
        vat_calculation_type:
          catalogItem.vat_calculation_type || "included",
      };
    },

    // Стрелка вниз открывает dropdown и циклически ведет к следующему варианту.
    highlightNext() {
      if (!this.isOpen) {
        this.openSuggestions();
        return;
      }
      if (this.catalogItems.length === 0) return;

      this.highlightedIndex =
        (this.highlightedIndex + 1) % this.catalogItems.length;
    },

    // Стрелка вверх идет назад, а из пустого состояния выбирает последний вариант.
    highlightPrevious() {
      if (!this.isOpen) {
        this.openSuggestions();
        return;
      }
      if (this.catalogItems.length === 0) return;

      if (this.highlightedIndex === -1) {
        this.highlightedIndex = this.catalogItems.length - 1;
        return;
      }

      this.highlightedIndex =
        (this.highlightedIndex - 1 + this.catalogItems.length) %
        this.catalogItems.length;
    },

    handleDocumentMouseDown(event) {
      if (!this.isOpen) return;
      if (this.$refs.root?.contains(event.target)) return;
      if (this.$refs.dropdown?.contains(event.target)) return;

      this.closeSuggestions();
    },

    updateDropdownPosition() {
      const input = this.$refs.input?.$el?.querySelector("input");
      if (!input) return;

      const rect = input.getBoundingClientRect();
      this.dropdownStyles = {
        left: `${rect.left}px`,
        top: `${rect.bottom + 6}px`,
        width: `${rect.width}px`,
      };
    },

    itemTypeLabel(baseType) {
      const itemType = this.repairItemTypeForCatalogBaseType(baseType);
      const option = this.itemTypeOptions.find(
        (item) => item.value === itemType
      );

      return option?.label || itemType;
    },

    catalogBaseTypeForRepairItemType(itemType) {
      // В ремонте работа называется labor, а в каталоге тот же тип — service.
      if (!itemType) return null;
      if (itemType === "labor") return "service";

      return itemType;
    },

    repairItemTypeForCatalogBaseType(baseType) {
      // Обратный маппинг нужен при выборе позиции каталога в строку ремонта.
      if (!baseType) return null;
      if (baseType === "service") return "labor";

      return baseType;
    },

    vatLabel(item) {
      if (item.vat_kind === "no_vat") return "Без НДС";
      if (item.vat_kind === "zero") return "НДС 0%";

      return `НДС ${item.vat_rate || 0}%`;
    },

    formatPrice(value) {
      return new Intl.NumberFormat("ru-RU", {
        maximumFractionDigits: 2,
      }).format(Number(value || 0));
    },

    normalizedText(value) {
      return String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
    },
  },
};
</script>

<style scoped>
.repair-item-name-combobox {
  min-width: 0;
}

.repair-item-name-combobox__dropdown {
  position: fixed;
  z-index: 4000;
  max-height: 280px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid #d7dde5;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.18);
}

.repair-item-name-combobox__option {
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--color-dark);
  cursor: pointer;
  text-align: left;
}

.repair-item-name-combobox__option:hover,
.repair-item-name-combobox__option--highlighted {
  background: #f3f7f5;
}

.repair-item-name-combobox__option-main,
.repair-item-name-combobox__option-meta {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.repair-item-name-combobox__option-main strong {
  overflow: hidden;
  font-size: 14px;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.repair-item-name-combobox__option-main span,
.repair-item-name-combobox__option-meta {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.25;
}

.repair-item-name-combobox__option-meta {
  flex: 0 0 auto;
  align-items: flex-end;
}

.repair-item-name-combobox__empty {
  padding: 10px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.35;
}
</style>
